import { TranscriptUtterance, EditorWord } from '../types';
import { apiClient } from './api';

interface TranscriptUtterancesResponse {
  data: TranscriptUtterance[];
}

interface TranscriptUtteranceResponse {
  data: TranscriptUtterance;
}

class UtterancesService {
  private baseUrl = '/transcripts';

  async getUtterances(audioFileId: string): Promise<TranscriptUtterance[]> {
    const response = await apiClient.get<TranscriptUtterancesResponse>(
      `${this.baseUrl}/${audioFileId}/utterances`
    );
    return response.data.data;
  }

  async updateUtteranceSpeaker(
    audioFileId: string,
    utteranceId: string,
    speakerId: string
  ): Promise<{ data: TranscriptUtterance }> {
    const response = await apiClient.put<TranscriptUtteranceResponse>(
      `${this.baseUrl}/${audioFileId}/utterances/${utteranceId}`,
      {
        speaker_id: speakerId,
      }
    );
    return response.data;
  }

  async splitUtterance(
    audioFileId: string,
    utteranceId: string,
    firstWordId: string,
    newUtteranceId: string
  ): Promise<{
    data: {
      original_utterance: TranscriptUtterance;
      new_utterance: TranscriptUtterance;
    };
  }> {
    const response = await apiClient.post<{
      data: {
        original_utterance: TranscriptUtterance;
        new_utterance: TranscriptUtterance;
      };
    }>(`${this.baseUrl}/${audioFileId}/utterances/${utteranceId}/split`, {
      first_word_id: firstWordId,
      new_utterance_id: newUtteranceId,
    });
    return response.data;
  }

  async mergeUtterances(
    audioFileId: string,
    targetUtteranceId: string
  ): Promise<{ data: { merged_utterance: TranscriptUtterance } }> {
    const response = await apiClient.post<{
      data: { merged_utterance: TranscriptUtterance };
    }>(
      `${this.baseUrl}/${audioFileId}/utterances/${targetUtteranceId}/merge`,
      {}
    );
    return response.data;
  }

  async updateUtteranceWords(
    audioFileId: string,
    utteranceId: string,
    editorWords: EditorWord[]
  ): Promise<TranscriptUtteranceResponse> {
    // Transform EditorWord array to backend format
    const words = editorWords.map((word) => {
      if (word.isNew) {
        // New word: send all fields
        return {
          id: word.id,
          text: word.text,
          start_time: word.startTime,
          end_time: word.endTime,
          confidence_score: word.confidenceScore,
        };
      } else if (word.isModified) {
        // Modified word: send id, changed text, updated endTime, and confidence_score
        return {
          id: word.id,
          text: word.text,
          end_time: word.endTime,
          confidence_score: word.confidenceScore,
        };
      } else {
        // Unchanged word: only send id
        return { id: word.id };
      }
    });

    const response = await apiClient.patch<TranscriptUtteranceResponse>(
      `${this.baseUrl}/${audioFileId}/utterances/${utteranceId}/update_words`,
      {
        words,
      }
    );
    return response.data;
  }
}

export const utterancesService = new UtterancesService();
